import {computePosition, autoUpdate, offset, flip, shift, arrow} from '@floating-ui/dom';

/**
 * Popover (tooltip) component behaviour.
 *
 * Renders a contextual information bubble anchored to a trigger. Positioning is
 * delegated to Floating UI (cross-browser), while the native Popover API is used
 * for top-layer rendering so the bubble is never clipped by `overflow: hidden`
 * ancestors (e.g. scrollable admin tables).
 *
 * Semantics are always those of a tooltip (`role="tooltip"`, non-interactive
 * text content). The `trigger` prop only controls HOW it opens:
 * - `hover` (default): hover + keyboard focus on pointer devices, tap on touch.
 * - `click`: tap/click toggles; native light-dismiss closes it.
 *
 * @see resources/views/components/ui/popover.blade.php
 */

/** Feature-detect the native Popover API. All targeted browsers support it; the class fallback keeps us safe otherwise. */
const SUPPORTS_POPOVER = typeof HTMLElement !== 'undefined'
    && Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover');

/** Grace period (ms) before a hover popover closes, so the pointer can travel onto the bubble (WCAG 1.4.13 "hoverable"). */
const HOVER_CLOSE_DELAY = 120;

/** Distance (px) between the trigger and the bubble body (Figma: 10px for all breakpoints). */
const BUBBLE_OFFSET = 10;

/**
 * How far (px) the 10px arrow square extends past the bubble edge, tuned so the arrow tip lands
 * ~4px from the trigger (Figma spec). Kept in sync with the arrow size in popover.css.
 */
const ARROW_STATIC_OFFSET = 5;

/** Padding (px) kept between the bubble and the viewport edges. */
const VIEWPORT_PADDING = 8;

const ALLOWED_PLACEMENTS = new Set([
    'top', 'bottom', 'left', 'right',
    'top-start', 'top-end', 'bottom-start', 'bottom-end',
    'left-start', 'left-end', 'right-start', 'right-end',
]);

/** Maps a resolved placement side to the bubble side the arrow attaches to. */
const STATIC_SIDE = {top: 'bottom', right: 'left', bottom: 'top', left: 'right'};

/** Currently-open popovers. We keep at most one open at a time (see closeOtherPopovers). */
const openPopovers = new Set();

/**
 * Close every open popover except the one passed in. Guarantees a single popover
 * is visible at a time regardless of trigger mode (hover/click) or input (mouse/touch).
 * @param {Popover} [current]
 * @returns {void}
 */
function closeOtherPopovers(current) {
    [...openPopovers].forEach((popover) => {
        if (popover !== current) {
            popover.close();
        }
    });
}

/**
 * Resolve the `position` prop into a Floating UI placement.
 * `auto` (and any unknown value) resolves to `top`, then flip() adjusts as needed.
 * @param {string|undefined} position
 * @returns {import('@floating-ui/dom').Placement}
 */
export function resolvePlacement(position) {
    if (!position || position === 'auto') {
        return 'top';
    }

    return ALLOWED_PLACEMENTS.has(position) ? position : 'top';
}

class Popover {
    /**
     * @param {HTMLElement} root - The `[data-popover]` wrapper element.
     */
    constructor(root) {
        this.root = root;
        this.trigger = root.querySelector('[data-popover-anchor]');
        this.bubble = root.querySelector('[data-popover-bubble]');
        this.arrowEl = this.bubble ? this.bubble.querySelector('[data-popover-arrow]') : null;

        if (!this.trigger || !this.bubble) {
            return;
        }

        // The [data-popover-anchor] wrapper isn't focusable, so mirror the description onto the first
        // focusable trigger element — otherwise screen readers won't announce it on keyboard focus.
        const describedBy = this.trigger.getAttribute('aria-describedby');
        const focusable = this.trigger.querySelector('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (describedBy && focusable) {
            const existing = focusable.getAttribute('aria-describedby');
            if (!existing?.split(' ').includes(describedBy)) {
                focusable.setAttribute('aria-describedby', existing ? `${existing} ${describedBy}` : describedBy);
            }
        }

        this.mode = root.dataset.popoverTrigger === 'click' ? 'click' : 'hover';
        this.placement = resolvePlacement(root.dataset.popoverPosition);
        this.isOpen = false;
        this.lastPointerType = 'mouse';
        this.cleanupAutoUpdate = null;
        this.closeTimer = null;

        this.bindEvents();
    }

    bindEvents() {
        // Keep the bubble's open/closed lifecycle in sync even when the browser
        // light-dismisses an `auto` popover (outside click / Escape).
        this.bubble.addEventListener('toggle', (event) => {
            if (event.newState === 'open') {
                this.onOpened();
            } else {
                this.onClosed();
            }
        });

        this.trigger.addEventListener('pointerdown', (event) => {
            this.lastPointerType = event.pointerType || 'mouse';
        });

        if (this.mode === 'click') {
            this.trigger.addEventListener('click', (event) => {
                if (this.shouldIgnoreTriggerClick(event)) {
                    return;
                }
                event.preventDefault();
                this.toggle();
            });
            return;
        }

        // Hover mode.
        this.trigger.addEventListener('pointerenter', (event) => {
            if (event.pointerType === 'mouse') {
                this.open();
            }
        });
        this.trigger.addEventListener('pointerleave', (event) => {
            if (event.pointerType === 'mouse') {
                this.scheduleClose();
            }
        });
        this.trigger.addEventListener('focusin', () => this.open());
        this.trigger.addEventListener('focusout', () => this.scheduleClose());

        // Touch devices have no hover: a tap toggles the popover.
        this.trigger.addEventListener('click', (event) => {
            if (this.lastPointerType === 'mouse' || this.shouldIgnoreTriggerClick(event)) {
                return;
            }
            event.preventDefault();
            this.toggle();
        });

        // Keep it open while the pointer is over the bubble itself.
        this.bubble.addEventListener('pointerenter', () => this.cancelScheduledClose());
        this.bubble.addEventListener('pointerleave', (event) => {
            if (event.pointerType === 'mouse') {
                this.scheduleClose();
            }
        });
    }

    /**
     * Don't hijack clicks meant for interactive elements inside the trigger
     * (links, buttons, form controls) — let them do their job.
     * @param {MouseEvent} event
     * @returns {boolean}
     */
    shouldIgnoreTriggerClick(event) {
        const target = event.target;
        // Let genuinely navigational/interactive descendants act instead of toggling — but NOT
        // plain buttons, which are the usual trigger element and must toggle the popover.
        return target instanceof Element
            && target.closest('a[href], input, select, textarea, label') !== null;
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.cancelScheduledClose();
        if (this.isOpen) {
            return;
        }

        // Only one popover open at a time. Closing others up front (not just relying on
        // the native `auto` behaviour) keeps hover, click and touch consistent.
        closeOtherPopovers(this);

        if (SUPPORTS_POPOVER) {
            this.bubble.showPopover();
        } else {
            this.bubble.classList.add('is-open');
            this.onOpened();
        }
    }

    close() {
        this.cancelScheduledClose();
        if (!this.isOpen) {
            return;
        }

        if (SUPPORTS_POPOVER) {
            if (this.bubble.matches(':popover-open')) {
                this.bubble.hidePopover();
            }
        } else {
            this.bubble.classList.remove('is-open');
            this.onClosed();
        }
    }

    onOpened() {
        this.isOpen = true;
        openPopovers.add(this);
        this.startAutoUpdate();
    }

    onClosed() {
        this.isOpen = false;
        openPopovers.delete(this);
        this.stopAutoUpdate();
        // Hide again until the next open re-positions it.
        this.bubble.removeAttribute('data-popover-positioned');
    }

    scheduleClose() {
        this.cancelScheduledClose();
        this.closeTimer = window.setTimeout(() => {
            // Don't close while the user is still interacting with either element.
            if (this.trigger.matches(':hover') || this.bubble.matches(':hover') || this.root.contains(document.activeElement)) {
                return;
            }
            this.close();
        }, HOVER_CLOSE_DELAY);
    }

    cancelScheduledClose() {
        if (this.closeTimer !== null) {
            window.clearTimeout(this.closeTimer);
            this.closeTimer = null;
        }
    }

    startAutoUpdate() {
        this.stopAutoUpdate();
        this.cleanupAutoUpdate = autoUpdate(this.trigger, this.bubble, () => this.position());
    }

    stopAutoUpdate() {
        if (this.cleanupAutoUpdate) {
            this.cleanupAutoUpdate();
            this.cleanupAutoUpdate = null;
        }
    }

    async position() {
        const middleware = [
            offset(BUBBLE_OFFSET),
            flip({padding: VIEWPORT_PADDING}),
            shift({padding: VIEWPORT_PADDING}),
        ];

        if (this.arrowEl) {
            middleware.push(arrow({element: this.arrowEl, padding: 6}));
        }

        const {x, y, placement, middlewareData} = await computePosition(this.trigger, this.bubble, {
            strategy: 'fixed',
            placement: this.placement,
            middleware,
        });

        // computePosition is async: bail if the popover was closed while it resolved, so we don't
        // apply stale coordinates or re-set data-popover-positioned on a hidden bubble.
        if (!this.isOpen) {
            return;
        }

        Object.assign(this.bubble.style, {left: `${x}px`, top: `${y}px`});

        this.positionArrow(placement, middlewareData.arrow);

        // Reveal only once positioned, so it never flashes at the pre-computed (top-left) spot.
        this.bubble.setAttribute('data-popover-positioned', '');
    }

    /**
     * @param {import('@floating-ui/dom').Placement} placement
     * @param {{x?: number, y?: number}|undefined} arrowData
     */
    positionArrow(placement, arrowData) {
        if (!this.arrowEl || !arrowData) {
            return;
        }

        const side = placement.split('-')[0];
        const staticSide = STATIC_SIDE[side];

        Object.assign(this.arrowEl.style, {
            left: arrowData.x != null ? `${arrowData.x}px` : '',
            top: arrowData.y != null ? `${arrowData.y}px` : '',
            right: '',
            bottom: '',
            [staticSide]: `${-ARROW_STATIC_OFFSET}px`,
        });

        // Drives which two borders form the outward "V" (see popover.blade.php styles).
        this.arrowEl.dataset.side = staticSide;
    }
}

/**
 * Initialise every not-yet-initialised popover in the given root.
 * @param {Document|Element} [root]
 * @returns {void}
 */
export function initPopovers(root = document) {
    root.querySelectorAll('[data-popover]:not([data-popover-initialized])').forEach((element) => {
        element.setAttribute('data-popover-initialized', '');
        new Popover(element);
    });
}

/**
 * Initialise the popovers inside a `cloneNode()` copy. The copy carries the source's init marker, bubble ids
 * and open state but none of its listeners, so `initPopovers()` alone would skip it.
 * @param {Element} root
 * @returns {void}
 */
export function initClonedPopovers(root) {
    root.querySelectorAll('[data-popover][data-popover-initialized]').forEach((element) => {
        element.removeAttribute('data-popover-initialized');

        const bubble = element.querySelector('[data-popover-bubble]');
        if (!bubble) {
            return;
        }

        const sourceId = bubble.id;
        const cloneId = `popover-${crypto.randomUUID()}`;
        bubble.id = cloneId;
        bubble.classList.remove('is-open');
        bubble.removeAttribute('data-popover-positioned');

        // Covers the anchor and the focusable trigger the source's constructor already mirrored the id onto.
        element.querySelectorAll('[aria-describedby]').forEach((described) => {
            const ids = described.getAttribute('aria-describedby').split(' ').map((id) => (id === sourceId ? cloneId : id));
            described.setAttribute('aria-describedby', ids.join(' '));
        });
    });

    initPopovers(root);
}

/**
 * Close every open popover on Escape (keyboard dismissal, WCAG 1.4.13).
 * @param {KeyboardEvent} event
 * @returns {void}
 */
function handleEscape(event) {
    if (event.key !== 'Escape') {
        return;
    }

    // Close through the instances so the full lifecycle runs (state, openPopovers, autoUpdate cleanup)
    // in both the native and fallback paths.
    [...openPopovers].forEach((popover) => popover.close());
}

/**
 * Initialise popovers present at load and wire the global dismiss listeners.
 * @returns {void}
 */
function boot() {
    initPopovers();

    document.addEventListener('keydown', handleEscape);

    // Light dismiss: a pointer press outside the open popover closes it. This gives
    // touch users (no hover) a reliable way to dismiss, and closes on desktop click-away.
    document.addEventListener('pointerdown', (event) => {
        if (openPopovers.size === 0 || !(event.target instanceof Node)) {
            return;
        }
        [...openPopovers].forEach((popover) => {
            if (!popover.root.contains(event.target)) {
                popover.close();
            }
        });
    }, true);

    // Catch popovers injected after the initial load (e.g. AJAX-rendered admin tables).
    // Batched with requestAnimationFrame so a burst of mutations triggers a single re-scan.
    let rescanScheduled = false;
    const observer = new MutationObserver((mutations) => {
        if (rescanScheduled || !mutations.some((mutation) => mutation.addedNodes.length > 0)) {
            return;
        }
        rescanScheduled = true;
        requestAnimationFrame(() => {
            rescanScheduled = false;
            initPopovers();
        });
    });
    observer.observe(document.body, {childList: true, subtree: true});
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
